export const DEVICE_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
const UUID_DEVICE_ID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89aAbB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const pause = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export function isValidDeviceId(value) {
  return DEVICE_ID_PATTERN.test(String(value || '').trim());
}

export function normalizeDeviceId(value) {
  const deviceId = String(value || '').trim();
  if (!isValidDeviceId(deviceId)) return '';
  return UUID_DEVICE_ID_PATTERN.test(deviceId) ? deviceId.toLowerCase() : deviceId;
}

function memoryDeviceId() {
  return typeof window === 'undefined' ? '' : normalizeDeviceId(window.__CURRENT_DEVICE_ID__);
}

export function readStoredDeviceId() {
  try {
    const stored = normalizeDeviceId(localStorage.getItem('deviceId'));
    if (isValidDeviceId(stored)) return stored;
  } catch { /* WebView 隐私模式下 localStorage 可能暂不可用 */ }
  const memory = memoryDeviceId();
  return isValidDeviceId(memory) ? memory : '';
}

export function saveDeviceId(value, source = 'unknown') {
  const deviceId = normalizeDeviceId(value);
  if (!deviceId) return '';
  if (typeof window !== 'undefined') window.__CURRENT_DEVICE_ID__ = deviceId;
  try { localStorage.setItem('deviceId', deviceId); } catch { /* 内存副本继续可用 */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('troncoin:device-id-ready', { detail: { deviceId, source } }));
  }
  return deviceId;
}

export function readUrlDeviceId(locationLike = window.location) {
  const url = new URL(locationLike.href);
  const queryValue = normalizeDeviceId(url.searchParams.get('deviceId'));
  if (isValidDeviceId(queryValue)) return queryValue;

  const rawHash = url.hash.replace(/^#/, '');
  if (!rawHash || rawHash.startsWith('/')) return '';
  return normalizeDeviceId(new URLSearchParams(rawHash).get('deviceId'));
}

export function removeDeviceIdFromAddressBar(locationLike = window.location, historyLike = window.history) {
  const url = new URL(locationLike.href);
  url.searchParams.delete('deviceId');
  const rawHash = url.hash.replace(/^#/, '');
  if (rawHash && !rawHash.startsWith('/')) {
    const hashParams = new URLSearchParams(rawHash);
    hashParams.delete('deviceId');
    url.hash = hashParams.toString() ? `#${hashParams}` : '';
  }
  historyLike.replaceState(historyLike.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export function captureUrlDeviceId() {
  if (typeof window === 'undefined') return '';
  const deviceId = readUrlDeviceId();
  if (!deviceId) return '';
  saveDeviceId(deviceId, 'url');
  removeDeviceIdFromAddressBar();
  return deviceId;
}

export function isAppWebView(userAgent = navigator.userAgent) {
  const ua = String(userAgent || '');
  const iosWebView = /(iPhone|iPad|iPod)/i.test(ua)
    && /AppleWebKit/i.test(ua)
    && /Mobile/i.test(ua)
    && !/(Safari|CriOS|FxiOS|EdgiOS|OPiOS)/i.test(ua);
  const androidWebView = /Android/i.test(ua) && (/\bwv\b/i.test(ua) || /Version\/4\.0/i.test(ua));
  return iosWebView || androidWebView;
}

async function waitForBridgeDeviceId(checks = 5, intervalMs = 650) {
  for (let index = 0; index < checks; index += 1) {
    if (index) await pause(intervalMs);
    const deviceId = readStoredDeviceId();
    if (deviceId) return deviceId;
  }
  return '';
}

async function fallbackRequest(locale) {
  const response = await fetch('/internal/device-id/resolve', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ locale })
  });
  const text = await response.text();
  let payload = {};
  try { payload = text ? JSON.parse(text) : {}; } catch { payload = {}; }
  if (!response.ok) {
    const error = new Error(payload.message || '暂时无法识别设备码');
    error.status = response.status;
    error.code = payload.code;
    throw error;
  }
  if (!isValidDeviceId(payload.deviceId)) throw Object.assign(new Error('设备码格式无效'), { code: 'INVALID_DEVICE_ID' });
  return normalizeDeviceId(payload.deviceId);
}

async function resolveFromServer(locale) {
  try {
    return await fallbackRequest(locale);
  } catch (error) {
    if (![404, 409, 503].includes(error.status)) throw error;
    await pause(2000);
    return fallbackRequest(locale);
  }
}

export async function resolveDeviceId(locale = 'zh-CN') {
  const urlDeviceId = captureUrlDeviceId();
  if (urlDeviceId) return { deviceId: urlDeviceId, source: 'url' };

  const bridgeDeviceId = await waitForBridgeDeviceId();
  if (bridgeDeviceId) {
    saveDeviceId(bridgeDeviceId, 'localStorage');
    return { deviceId: bridgeDeviceId, source: 'localStorage' };
  }

  if (!isAppWebView()) return { deviceId: '', source: 'unavailable' };
  try {
    const deviceId = await resolveFromServer(locale);
    saveDeviceId(deviceId, 'server-fallback');
    return { deviceId, source: 'server-fallback' };
  } catch {
    return { deviceId: '', source: 'pending' };
  }
}

export function monitorDeviceIdChanges(initialDeviceId, onChange, { checks = 8, intervalMs = 650 } = {}) {
  let current = normalizeDeviceId(initialDeviceId);
  let stopped = false;
  let running = false;

  const inspect = async () => {
    const next = readStoredDeviceId();
    if (!next || next === current || running || stopped) return;
    running = true;
    try {
      await onChange(next);
      current = next;
    } finally {
      running = false;
    }
  };

  const readyListener = () => { inspect().catch(() => {}); };
  const storageListener = (event) => {
    if (event.key === 'deviceId') inspect().catch(() => {});
  };
  window.addEventListener('troncoin:device-id-ready', readyListener);
  window.addEventListener('storage', storageListener);

  let completed = 0;
  const timer = window.setInterval(() => {
    completed += 1;
    inspect().catch(() => {});
    if (completed >= checks) window.clearInterval(timer);
  }, intervalMs);

  return () => {
    stopped = true;
    window.clearInterval(timer);
    window.removeEventListener('troncoin:device-id-ready', readyListener);
    window.removeEventListener('storage', storageListener);
  };
}
